/**
 * Police Departments API Service - Infinite scroll wrapper
 *
 * This service wraps the generated police departments API with infinite query support.
 * The backend provides a standard paginated list endpoint, and we wrap it with
 * RTK Query's infiniteQuery for smooth infinite scrolling on the frontend.
 *
 * Pattern follows tasksService.ts approach.
 *
 * Also enhances the generated API with proper cache invalidation for records channels.
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';
import {
  enhancedApi as generatedApi,
  type ReadPoliceDepartmentWithStats,
} from './generated/policeDepartmentsService';

export interface PoliceDepartmentsFilter {
  search?: string;
  state_code?: string;
  enrichment_status?: string;
  do_not_call?: boolean;
  ordering?: string;
}

export interface PoliceDepartmentsSearchResults {
  count: number;
  results: ReadPoliceDepartmentWithStats[];
}

export const policeDepartmentsApiClient = createApi({
  reducerPath: 'policeDepartmentsApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['PoliceDepartments'],
  endpoints: (builder) => ({
    getPoliceDepartmentsInfinite: builder.infiniteQuery<
      PoliceDepartmentsSearchResults,
      PoliceDepartmentsFilter,
      {
        offset: number;
        limit: number;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: {
          offset: 0,
          limit: 100,
        },
        getNextPageParam: (lastPage, pages, lastPageParam) => {
          const totalFetched = lastPageParam.offset + lastPageParam.limit;
          if (totalFetched >= lastPage.count) return undefined;
          return {
            offset: totalFetched,
            limit: lastPageParam.limit,
          };
        },
        getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
          if (firstPageParam.offset === 0) return undefined;
          return {
            offset: Math.max(0, firstPageParam.offset - firstPageParam.limit),
            limit: firstPageParam.limit,
          };
        },
      },
      query: ({ queryArg, pageParam }) => {
        const params = new URLSearchParams({
          limit: pageParam.limit.toString(),
          offset: pageParam.offset.toString(),
        });

        if (queryArg.search) params.set('search', queryArg.search);
        if (queryArg.state_code) params.set('state_code', queryArg.state_code);
        if (queryArg.enrichment_status)
          params.set('enrichment_status', queryArg.enrichment_status);
        if (queryArg.do_not_call !== undefined)
          params.set('do_not_call', queryArg.do_not_call.toString());
        if (queryArg.ordering) params.set('ordering', queryArg.ordering);

        return {
          url: `api/police-departments/?${params.toString()}`,
        };
      },
      providesTags: (result, error, filter) => [
        {
          type: 'PoliceDepartments',
          id: JSON.stringify({ ...filter, offset: undefined }),
        },
        { type: 'PoliceDepartments' },
      ],
    }),
  }),
});

export const { useGetPoliceDepartmentsInfiniteInfiniteQuery } =
  policeDepartmentsApiClient;

/**
 * Enhanced version of the generated API with proper cache invalidation.
 *
 * This ensures that when records channels are created/updated/deleted,
 * the parent department query is invalidated and refetches, so the UI
 * updates immediately.
 */
export const enhancedPoliceDepartmentsApi = generatedApi
  .injectEndpoints({
    endpoints: (build) => ({
      // Search police departments (action endpoint not in generated API yet)
      searchPoliceDepartments: build.query<
        ReadPoliceDepartmentWithStats[],
        { query: string }
      >({
        query: ({ query }) => ({
          url: 'api/police-departments/search/',
          params: { q: query },
        }),
        providesTags: ['police_departments'],
      }),
    }),
    overrideExisting: false,
  })
  .enhanceEndpoints({
    endpoints: {
      // Provide specific tags for department queries so we can invalidate them by ID
      policeDepartmentsRetrieve: {
        providesTags: (result: unknown, error: unknown, arg: { id: string }) =>
          result
            ? [
                { type: 'police_departments' as const, id: arg.id },
                'police_departments' as const,
              ]
            : ['police_departments' as const],
      },
      // Enhance records channel mutations to invalidate both channel tags
      // AND the specific department tag so the department detail view refetches
      policeRecordsChannelsCreate: {
        invalidatesTags: (
          result: unknown,
          error: unknown,
          arg: { departmentId: string },
        ) => [
          'police_records_channels' as const,
          { type: 'police_departments' as const, id: arg.departmentId },
          'police_departments' as const,
        ],
      },
      policeRecordsChannelsUpdate: {
        invalidatesTags: (
          result: unknown,
          error: unknown,
          arg: { departmentId: string },
        ) => [
          'police_records_channels' as const,
          { type: 'police_departments' as const, id: arg.departmentId },
          'police_departments' as const,
        ],
      },
      policeRecordsChannelsDestroy: {
        invalidatesTags: (
          result: unknown,
          error: unknown,
          arg: { departmentId: string },
        ) => [
          'police_records_channels' as const,
          { type: 'police_departments' as const, id: arg.departmentId },
          'police_departments' as const,
        ],
      },
    },
  });

export const enhancedApi = enhancedPoliceDepartmentsApi;

// Export all hooks
export const {
  // Search (injected)
  useSearchPoliceDepartmentsQuery,
  // Departments
  usePoliceDepartmentsActiveMattersQuery,
  usePoliceDepartmentsCreateMutation,
  usePoliceDepartmentsGetOrCreateMutation,
  usePoliceDepartmentsListQuery,
  usePoliceDepartmentsListWithStatsQuery,
  usePoliceDepartmentsPartialUpdateMutation,
  usePoliceDepartmentsRetrieveQuery,
  usePoliceDepartmentsSearchQuery,
  usePoliceDepartmentsTriggerEnrichmentMutation,
  usePoliceDepartmentsUpdateMutation,
  // Records Channels
  usePoliceRecordsChannelsCreateMutation,
  usePoliceRecordsChannelsDestroyMutation,
  usePoliceRecordsChannelsListQuery,
  usePoliceRecordsChannelsRetrieveQuery,
  usePoliceRecordsChannelsUpdateMutation,
} = enhancedPoliceDepartmentsApi;
